import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../contexts/I18nContext';
import { useAuthCheck } from '../../../hooks/useAuthCheck';
import { usePolling } from '../../../hooks/usePolling';
import { useCreditConfig } from '../../../contexts/CreditConfigContext';
import { handleUnifiedError } from '../../../utils/unifiedErrorHandler';
import axios from '../../../services/api';
import toast from 'react-hot-toast';
import ErrorModal from '../../common/ErrorModal';
import LoginModal from '../../Login/LoginModal';
import ShareTextModal from '../../common/ShareTextModal';
import { options } from './options';
import { API_BASE_URL } from '../../../lib/constants';
import FaceSwapLeftPanel from './components/FaceSwapLeftPanel';
import FaceSwapRightPanel from './components/FaceSwapRightPanel';
import FaceSwapPreviewModal from './components/FaceSwapPreviewModal';
import { canGenerateFaceSwap, getRandomSelectedOptions, buildCombinedPrompt, handleImageUploadChange } from './faceSwapUtils';
import SizeSelector from '../../AiToolTemplate/components/SizeSelector/SizeSelector';
import './styles/index.scss';

const FaceSwap = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { checkAuthAndExecute, showLoginModal, handleCloseLogin, handleLoginSuccess } = useAuthCheck();
  const { getImageGenerationCredits } = useCreditConfig();
  
  const [activeTab, setActiveTab] = useState('role');
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null); // 保存原始File对象
  const [mainTab, setMainTab] = useState('text-to-image'); // 'text-to-image' or 'image-to-image'
  // 文生图与图生图独立状态
  const [generatedImageText, setGeneratedImageText] = useState(null);
  const [generatedImageImage, setGeneratedImageImage] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [promptText, setPromptText] = useState('');
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generationProgressText, setGenerationProgressText] = useState(0);
  const [generationProgressImage, setGenerationProgressImage] = useState(0);
  const [showUploadErrorModal, setShowUploadErrorModal] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, mediaUrl: '', mediaType: 'image' });
  const [shareUrlText, setShareUrlText] = useState('');
  const [shareUrlImage, setShareUrlImage] = useState('');
  const [generationSettings, setGenerationSettings] = useState(null);
  const toolCost = getImageGenerationCredits({
    toolId: 'face-swap',
    model: generationSettings?.model,
    resolution: generationSettings?.resolution
  });

  // 定时器引用
  const progressIntervalRef = useRef(null);

  const completedImageUrl = (data) => data.imageUrl.startsWith('http') ? data.imageUrl : `${API_BASE_URL}${data.imageUrl}`;

  // 初始化轮询Hook
  const { startPolling, stopPolling } = usePolling({
    onSuccess: (data) => {
      const isTextMode = mainTab === 'text-to-image';
      if (isTextMode) {
        setGenerationProgressText(100);
        setTimeout(() => {
          setGeneratedImageText({ url: completedImageUrl(data), id: data.id });
          setShareUrlText(data.shareUrl || '');
          setIsGeneratingText(false);
          setGenerationProgressText(0);
          toast.success(t('aiWorkbench.faceSwap.messages.generationSuccess'));
        }, 500);
      } else {
        setGenerationProgressImage(100);
        setTimeout(() => {
          setGeneratedImageImage({ url: completedImageUrl(data), id: data.id });
          setShareUrlImage(data.shareUrl || '');
          setIsGeneratingImage(false);
          setGenerationProgressImage(0);
          toast.success(t('aiWorkbench.faceSwap.messages.generationSuccess'));
        }, 500);
      }
    },
    onFailed: (errorMessage) => {
      const isTextMode = mainTab === 'text-to-image';
      if (isTextMode) {
        setIsGeneratingText(false);
        setGenerationProgressText(0);
      } else {
        setIsGeneratingImage(false);
        setGenerationProgressImage(0);
      }
      // 不调用handleError，因为usePolling内部已经显示了错误toast
      // 只重置状态，避免重复弹窗
      console.error('Generation failed:', errorMessage);
    },
    onTimeout: () => {
      const isTextMode = mainTab === 'text-to-image';
      if (isTextMode) {
        setIsGeneratingText(false);
        setGenerationProgressText(0);
      } else {
        setIsGeneratingImage(false);
        setGenerationProgressImage(0);
      }
      // 不调用handleError，因为usePolling内部已经显示了错误toast
      // 只重置状态，避免重复弹窗
      console.error('Generation timeout:', t('aiWorkbench.faceSwap.messages.generationTimeout'));
    },
    onError: (error) => {
      const isTextMode = mainTab === 'text-to-image';
      if (isTextMode) {
        setIsGeneratingText(false);
        setGenerationProgressText(0);
      } else {
        setIsGeneratingImage(false);
        setGenerationProgressImage(0);
      }
      // 不调用handleError，因为usePolling内部已经显示了错误toast
      // 只重置状态，避免重复弹窗
      console.error('Polling error:', error);
    }
  });

  const clearTimers = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    stopPolling();
    setIsGeneratingText(false);
    setIsGeneratingImage(false);
    setGenerationProgressText(0);
    setGenerationProgressImage(0);
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const canGenerate = () => canGenerateFaceSwap({
    promptText,
    selectedOptions,
    mainTab,
    image
  });

  const handleImageUpload = (e) => {
    handleImageUploadChange({
      event: e,
      setImageFile,
      setImage,
      setShowUploadErrorModal
    });
  };

  const handleUploadAreaClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleRandomSelection = () => {
    const newSelectedOptions = getRandomSelectedOptions();
    setSelectedOptions(newSelectedOptions);
  };

  const handleClearAll = () => {
    setSelectedOptions({});
    setPromptText('');
  };

  const handleOptionChange = (childKey, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [childKey]: value
    }));
  };

  const combinePrompt = () => buildCombinedPrompt({
    selectedOptions,
    promptText,
    mainTab
  });

  const handleGenerate = () => {
    if (mainTab === 'image-to-image' && !imageFile) {
      toast.error(t('aiWorkbench.faceSwap.messages.uploadFirst'));
      return;
    }

    checkAuthAndExecute(async () => {
      const isTextMode = mainTab === 'text-to-image';
      const setGen = isTextMode ? setIsGeneratingText : setIsGeneratingImage;
      const setProg = isTextMode ? setGenerationProgressText : setGenerationProgressImage;
      const setImg = isTextMode ? setGeneratedImageText : setGeneratedImageImage;

      // 清理之前的定时器
      clearTimers();

      setGen(true);
      setProg(0);
      setImg(null);

      try {
        const combinedPrompt = combinePrompt();
        const rawSettings = generationSettings && typeof generationSettings === 'object' ? generationSettings : null;
        const finalAspectRatio = typeof rawSettings?.aspectRatio === 'string' ? rawSettings.aspectRatio : 'Auto';
        const finalModel = typeof rawSettings?.model === 'string' ? rawSettings.model : 'Gemini';
        const finalResolution = typeof rawSettings?.resolution === 'string' ? rawSettings.resolution : undefined;
        
        let result;
        if (isTextMode) {
          const requestData = {
            prompt: combinedPrompt,
            steps: 20,
            cfgScale: 7.5,
            style: 'realistic',
            aspectRatio: finalAspectRatio,
            model: finalModel,
            resolution: finalResolution,
            toolId: 'face-swap'
          };
          
          result = await axios.post('/generate-image', requestData);
        } else {
          const formData = new FormData();
          formData.append('prompt', combinedPrompt);
          formData.append('style', 'realistic');
          formData.append('toolId', 'face-swap');
          formData.append('aspectRatio', finalAspectRatio);
          if (finalModel) formData.append('model', finalModel);
          if (finalResolution) formData.append('resolution', finalResolution);
          formData.append('images', imageFile);
          
          result = await axios.post('/generate-image/edit', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        }

        const data = result.data;
        const generationId = data.id;
        
        if (generationId) {
          toast.success(t('aiWorkbench.faceSwap.messages.generationStarted'));
          startPolling(generationId, (progress) => {
            (isTextMode ? setGenerationProgressText : setGenerationProgressImage)(progress);
          });
        } else {
          clearTimers();
          throw new Error(t('aiWorkbench.faceSwap.messages.generateIdError'));
        }
        
      } catch (err) {
        clearTimers();
        handleUnifiedError(err, t, {
          showToast: true,
          fallbackMessage: t('aiWorkbench.faceSwap.messages.generationFailed')
        });
      }
    });
  };

  return (
    <div className="face-swap-container">
      <div className="page-header">
        <button className="back-button" onClick={() => navigate('/ai-tools-workbench')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t('aiWorkbench.faceSwap.buttons.backToWorkbench')}
        </button>
        
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">👤</span>
            {t('aiWorkbench.faceSwap.title')}
          </h1>
          <p className="page-description">
            {t('aiWorkbench.faceSwap.description')}
          </p>
        </div>
      </div>

      <div className="face-swap-main-content">
        <FaceSwapLeftPanel
          t={t}
          mainTab={mainTab}
          setMainTab={setMainTab}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          options={options}
          promptText={promptText}
          onPromptChange={setPromptText}
          generationSettingsNode={
            <div className="face-swap-generation-settings">
              <SizeSelector
                value={generationSettings}
                onChange={setGenerationSettings}
                disabled={mainTab === 'text-to-image' ? isGeneratingText : isGeneratingImage}
              />
            </div>
          }
          onRandomSelection={handleRandomSelection}
          onClearAll={handleClearAll}
          selectedOptions={selectedOptions}
          onOptionChange={handleOptionChange}
          isGenerating={mainTab === 'text-to-image' ? isGeneratingText : isGeneratingImage}
          generationProgress={mainTab === 'text-to-image' ? generationProgressText : generationProgressImage}
          canGenerate={canGenerate()}
          toolCost={toolCost}
          onGenerate={handleGenerate}
        />

        <FaceSwapRightPanel
          t={t}
          mainTab={mainTab}
          generatedImageText={generatedImageText}
          generatedImageImage={generatedImageImage}
          shareUrlText={shareUrlText}
          shareUrlImage={shareUrlImage}
          image={image}
          onUploadAreaClick={handleUploadAreaClick}
          onImageChange={handleImageUpload}
          setShareModal={setShareModal}
          setPreviewImageUrl={setPreviewImageUrl}
          setShowPreviewModal={setShowPreviewModal}
        />
      </div>

      <FaceSwapPreviewModal
        t={t}
        showPreviewModal={showPreviewModal}
        previewImageUrl={previewImageUrl}
        setShowPreviewModal={setShowPreviewModal}
      />

      {/* 登录弹窗 */}
      {showLoginModal && (
        <LoginModal 
          isOpen={showLoginModal}
          onClose={handleCloseLogin} 
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      <ErrorModal
        open={showUploadErrorModal}
        title={t('common.warning')}
        message={`${t('imageService.unsupportedFormat')}\n${t('imageService.fileTooLarge')}`}
        onClose={() => setShowUploadErrorModal(false)}
      />
      <ShareTextModal
        open={shareModal.open}
        title={t('creationRecord.enterShareText')}
        placeholder={t('creationRecord.enterShareText')}
        maxLength={2000}
        platform={null}
        mediaUrl={shareModal.mediaUrl}
        mediaType={shareModal.mediaType}
        by=""
        onConfirm={() => { setShareModal({ open: false, mediaUrl: '', mediaType: 'image' }); }}
        onCancel={() => { setShareModal({ open: false, mediaUrl: '', mediaType: 'image' }); }}
      />
    </div>
  );
};

export default FaceSwap;
